import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { useDesignStore } from '../../store/designStore';
import { useThinLineCheck } from '../../hooks/useThinLineCheck';
import { ThinLineDetailsModal } from '../canvas/ThinLineDetailsModal';
import type { FabricCanvas } from '../../types/fabric';
import { ToolSection } from '../tools/shared/ToolSection';

interface LayerEntry {
  uid:     string;
  type:    string;
  name:    string;
  visible: boolean;
  locked:  boolean;
  index:   number;
  hasThinLines: boolean;
}

interface LayerPanelProps {
  canvas: FabricCanvas | null;
  /** Fired after a layer row is clicked and its object becomes the active
   *  selection — lets the parent (RightPanel → DesignerApp) open the edit
   *  panel for it on the left side. */
  onLayerSelected?: () => void;
}

function getObjectName(obj: any, index: number, t: (key: string) => string): string {
  if (obj.name) return obj.name;
  const n = index + 1;
  if (obj.type === 'text' || obj.type === 'textbox')
    return (obj.text ?? '').slice(0, 20) || `${t('layers.textLabel')} ${n}`;
  if (obj.type === 'curved-text' || obj.type === 'curvedText')
    return (obj.text ?? '').slice(0, 16) || `${t('layers.curvedTextLabel')} ${n}`;
  if (obj.type === 'image') return obj._imageSrcLabel ?? `${t('layers.imageLabel')} ${n}`;
  if (obj.type === 'rect')  return `${t('layers.rectLabel')} ${n}`;
  if (obj.type === 'circle') return `${t('layers.circleLabel')} ${n}`;
  if (obj.type === 'path' || obj.type === 'group') return `${t('layers.shapeLabel')} ${n}`;
  return `${t('layers.objectLabel')} ${n}`;
}

function iconFor(type: string): string {
  if (type === 'text' || type === 'textbox') return 'T';
  if (type === 'curved-text' || type === 'curvedText') return '↷';
  if (type === 'image') return '🖼';
  if (type === 'rect') return '▭';
  if (type === 'circle') return '○';
  return '◈';
}

export function LayerPanel({ canvas, onLayerSelected }: LayerPanelProps) {
  const { t } = useTranslation();
  const { selectedObject, setSelectedObject } = useDesignStore();
  const activeSide = useDesignStore(state => state.sides.find(s => s.sideKey === state.activeSideKey));
  // Same hook DesignerApp.tsx's top banner reads from — a per-side derived
  // hook, so calling it again here isn't extra network work (the underlying
  // fetch/result cache is module-level, see useThinLineCheck.ts), just a
  // second read of the same already-computed flags for this row-level badge.
  const thinLineCheck = useThinLineCheck(activeSide);
  const [thinLineModalUid, setThinLineModalUid] = useState<string | null>(null);
  const [layers, setLayers] = useState<LayerEntry[]>([]);
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const [renamingUid, setRenamingUid] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const [draggedUid, setDraggedUid] = useState<string | null>(null);
  const [dragOverUid, setDragOverUid] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const buildLayers = useCallback(() => {
    if (!canvas) { setLayers([]); return; }
    const flaggedUids = new Set(thinLineCheck.flags.filter(f => !f.isBackground).map(f => f.uid));
    const objects: any[] = (canvas as any).getObjects() ?? [];
    const entries: LayerEntry[] = objects
      .filter(obj => !obj.isTemplateBackground)
      .map((obj, i) => {
        if (!obj.__uid) obj.__uid = `obj-${Date.now()}-${i}`;
        return {
          uid:     obj.__uid,
          type:    obj.type ?? 'object',
          name:    getObjectName(obj, i, t),
          visible: obj.visible !== false,
          locked:  obj.lockMovementX === true && obj.lockMovementY === true,
          index:   objects.indexOf(obj),
          hasThinLines: flaggedUids.has(obj.__uid),
        };
      })
      .reverse();
    setLayers(entries);
  }, [canvas, t, thinLineCheck.flags]);

  useEffect(() => {
    if (!canvas) return;
    canvas.on('object:added',    buildLayers);
    canvas.on('object:removed',  buildLayers);
    canvas.on('object:modified', buildLayers);
    canvas.on('selection:created', buildLayers);
    canvas.on('selection:cleared', buildLayers);
    // Undo/redo restore via Fabric's `loadFromJSON`, which repopulates objects
    // through `insertAt` rather than `add`/`remove` — none of the events above
    // fire for it, so without this the layer list goes stale (or briefly empty,
    // since `loadFromJSON` clears the canvas before re-adding) right after an
    // undo/redo. See historyStore.ts's `undo`/`redo` for where this fires.
    canvas.on('history:restored', buildLayers);
    buildLayers();
    return () => {
      canvas.off('object:added',    buildLayers);
      canvas.off('object:removed',  buildLayers);
      canvas.off('object:modified', buildLayers);
      canvas.off('selection:created', buildLayers);
      canvas.off('selection:cleared', buildLayers);
      canvas.off('history:restored', buildLayers);
    };
  }, [canvas, buildLayers]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenFor(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getObj = (index: number): any => ((canvas as any)?.getObjects() ?? [])[index];

  const selectLayer = (index: number) => {
    const obj = getObj(index);
    if (!obj || !canvas) return;
    (canvas as any).setActiveObject(obj);
    canvas.renderAll();
    setSelectedObject(obj);
    onLayerSelected?.();
  };

  const toggleVisibility = (index: number) => {
    const obj = getObj(index);
    if (!obj || !canvas) return;
    obj.set('visible', !obj.visible);
    canvas.renderAll();
    buildLayers();
  };

  const toggleLock = (index: number) => {
    const obj = getObj(index);
    if (!obj || !canvas) return;
    const locked = obj.lockMovementX === true;
    obj.set({
      lockMovementX: !locked, lockMovementY: !locked,
      lockScalingX: !locked,  lockScalingY: !locked,
      lockRotation: !locked,  selectable: locked, evented: locked,
    });
    canvas.renderAll();
    buildLayers();
  };

  // Drag-to-reorder — the handle (⠿) was previously decorative only (cursor:grab
  // styling with no actual drag wiring behind it). Dropping `draggedUid`'s layer
  // onto another row moves the real Fabric object to that row's z-order index via
  // `canvas.moveTo()`, the same primitive `bringForward`/`sendToBack` etc. build on.
  const moveLayer = (targetIndex: number) => {
    if (!canvas || !draggedUid) return;
    const draggedLayer = layers.find(l => l.uid === draggedUid);
    setDraggedUid(null);
    setDragOverUid(null);
    if (!draggedLayer || draggedLayer.index === targetIndex) return;
    const obj = getObj(draggedLayer.index);
    if (!obj) return;
    (canvas as any).moveTo(obj, targetIndex);
    canvas.renderAll();
    buildLayers();
  };

  const duplicateObj = (index: number) => {
    const obj = getObj(index);
    if (!obj || !canvas) return;
    obj.clone((clone: any) => {
      clone.set({ left: (obj.left ?? 0) + 16, top: (obj.top ?? 0) + 16 });
      canvas.add(clone);
      canvas.setActiveObject(clone);
      canvas.renderAll();
    });
    setMenuOpenFor(null);
  };

  const moveUp = (index: number) => {
    const obj = getObj(index);
    if (!obj || !canvas) return;
    (canvas as any).bringForward(obj);
    canvas.renderAll();
    canvas.fire('object:modified', { target: obj });
    buildLayers();
    setMenuOpenFor(null);
  };

  const moveDown = (index: number) => {
    const obj = getObj(index);
    if (!obj || !canvas) return;
    (canvas as any).sendBackwards(obj);
    canvas.renderAll();
    buildLayers();
    setMenuOpenFor(null);
  };

  const moveToFront = (index: number) => {
    const obj = getObj(index);
    if (!obj || !canvas) return;
    (canvas as any).bringToFront(obj);
    canvas.renderAll();
    buildLayers();
    setMenuOpenFor(null);
  };

  const moveToBack = (index: number) => {
    const obj = getObj(index);
    if (!obj || !canvas) return;
    (canvas as any).sendToBack(obj);
    canvas.renderAll();
    buildLayers();
    setMenuOpenFor(null);
  };

  const removeLayer = (index: number) => {
    const obj = getObj(index);
    if (!obj || !canvas) return;
    canvas.remove(obj);
    canvas.renderAll();
    setMenuOpenFor(null);
  };

  const startRename = (uid: string, currentName: string) => {
    setRenamingUid(uid);
    setRenameVal(currentName);
    setMenuOpenFor(null);
  };

  const commitRename = (index: number) => {
    const obj = getObj(index);
    if (obj) obj.name = renameVal.trim() || obj.name;
    setRenamingUid(null);
    buildLayers();
  };

  if (!canvas || layers.length === 0) {
    return (
      <div style={{ padding: 12, height: '100%', overflowY: 'auto' }}>
        <ToolSection title={t('layers.title')}>
          <div style={{ padding: '16px 0', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>≡</div>
            {t('layers.empty')}
          </div>
        </ToolSection>
      </div>
    );
  }

  const activeMenuLayer = layers.find(l => l.uid === menuOpenFor) ?? null;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', padding: '8px 12px 6px', flexShrink: 0 }}>
        {t('layers.title')}
      </div>
      {/* Scrollable layer list */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {layers.map(layer => {
          const isSelected = !!(selectedObject && (selectedObject as any).__uid === layer.uid);
          const isMenuOpen = menuOpenFor === layer.uid;
          const isRenaming = renamingUid === layer.uid;

          return (
            <div
              key={layer.uid}
              onClick={() => selectLayer(layer.index)}
              className="layer-row"
              draggable
              onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDraggedUid(layer.uid); }}
              onDragOver={e => {
                e.preventDefault();
                if (draggedUid && draggedUid !== layer.uid) setDragOverUid(layer.uid);
              }}
              onDragLeave={() => { if (dragOverUid === layer.uid) setDragOverUid(null); }}
              onDrop={e => { e.preventDefault(); moveLayer(layer.index); }}
              onDragEnd={() => { setDraggedUid(null); setDragOverUid(null); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '0 8px',
                height: 36,
                cursor: 'pointer',
                background: isSelected ? 'var(--selection-tint)' : '#f8fafc',
                borderLeft: isSelected ? '2px solid var(--selection)' : '2px solid transparent',
                borderTop: dragOverUid === layer.uid ? '2px solid var(--selection)' : '2px solid transparent',
                opacity: draggedUid === layer.uid ? 0.4 : 1,
                fontSize: 12,
              }}
            >
              {/* Drag handle */}
              <span style={{ color: 'rgba(0, 0, 0, 0.87)', fontSize: 14, flexShrink: 0, cursor: 'grab', userSelect: 'none' }}>⠿</span>

              {/* Type icon */}
              <span style={{
                width: 18, textAlign: 'center', fontSize: 14,
                color: 'rgba(0, 0, 0, 0.87)', flexShrink: 0,
                opacity: layer.visible ? 1 : 0.4,
              }}>
                {iconFor(layer.type)}
              </span>

              {/* Name / rename input */}
              {isRenaming ? (
                <input
                  autoFocus
                  value={renameVal}
                  onChange={e => setRenameVal(e.target.value)}
                  onBlur={() => commitRename(layer.index)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitRename(layer.index);
                    if (e.key === 'Escape') setRenamingUid(null);
                  }}
                  onClick={e => e.stopPropagation()}
                  style={{ flex: 1, fontSize: 12, padding: '2px 4px', border: '1px solid var(--selection)', borderRadius: 3, outline: 'none' }}
                />
              ) : (
                <span style={{
                  flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontSize: 12, color: 'rgba(0, 0, 0, 0.87)',
                  opacity: layer.visible ? 1 : 0.45,
                }}>
                  {layer.name}
                </span>
              )}

              {/* Thin-line warning badge — per direct user request ("display
                  the warning message on the object on the layer section and
                  on click of the warning highlight the thin line"). Opens
                  the same details modal DesignerApp.tsx's top banner uses,
                  scoped to just this object. */}
              {layer.hasThinLines && (
                <button
                  onClick={e => { e.stopPropagation(); setThinLineModalUid(layer.uid); }}
                  title={t('thinLine.layerBadgeTitle')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0, display: 'flex' }}
                >
                  <AlertTriangle size={13} style={{ color: '#f59e0b' }} />
                </button>
              )}

              {/* ⋮ trigger — always in DOM, opacity controlled by CSS */}
              <button
                onClick={e => {
                  e.stopPropagation();
                  if (isMenuOpen) { setMenuOpenFor(null); setMenuPos(null); return; }
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  setMenuPos({ top: rect.bottom + 2, right: window.innerWidth - rect.right });
                  setMenuOpenFor(layer.uid);
                }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '2px 4px', borderRadius: 3, fontSize: 15, color: 'rgba(0, 0, 0, 0.87)',
                  flexShrink: 0,
                  // Visible without hovering whenever this is the actively-selected
                  // row (e.g. right after adding text/an image) — previously this
                  // only ever appeared on :hover, so the reorder options (up/down/
                  // bring to front/send to back, etc.) were easy to miss entirely
                  // on the very object you just added.
                  opacity: (isMenuOpen || isSelected) ? 1 : 0,
                }}
                className="layer-menu-btn"
                title={t('layers.options')}
              >⋮</button>
            </div>
          );
        })}
      </div>

      {/* Context menu — rendered with position:fixed so it's never clipped by overflow:auto */}
      {menuOpenFor && activeMenuLayer && menuPos && (
        <>
          {/* Click-away backdrop */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 998 }}
            onClick={() => { setMenuOpenFor(null); setMenuPos(null); }}
          />
          <div
            ref={menuRef}
            onClick={e => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: menuPos.top,
              right: menuPos.right,
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              zIndex: 999,
              minWidth: 168,
              padding: '4px 0',
            }}
          >
            {([
              { label: t('layers.rename'),      action: () => startRename(activeMenuLayer.uid, activeMenuLayer.name) },
              { label: t('layers.duplicate'),    action: () => duplicateObj(activeMenuLayer.index) },
              { label: activeMenuLayer.locked ? t('layers.unlock') : t('layers.lock'), action: () => { toggleLock(activeMenuLayer.index); setMenuOpenFor(null); setMenuPos(null); } },
              { label: activeMenuLayer.visible ? t('layers.hide') : t('layers.show'), action: () => { toggleVisibility(activeMenuLayer.index); setMenuOpenFor(null); setMenuPos(null); } },
              { label: t('layers.up'),           action: () => moveUp(activeMenuLayer.index) },
              { label: t('layers.down'),         action: () => moveDown(activeMenuLayer.index) },
              { label: t('layers.bringToFront'), action: () => moveToFront(activeMenuLayer.index) },
              { label: t('layers.sendToBack'),   action: () => moveToBack(activeMenuLayer.index) },
              { label: t('layers.delete'),       action: () => removeLayer(activeMenuLayer.index), danger: true },
            ] as Array<{ label: string; action: () => void; danger?: boolean }>).map(item => (
              <button
                key={item.label}
                onClick={item.action}
                style={{
                  display: 'block', width: '100%', padding: '6px 14px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  textAlign: 'left', fontSize: 12,
                  color: item.danger ? '#ef4444' : '#374151',
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}

      {thinLineModalUid && thinLineCheck.flags.length > 0 && (
        <ThinLineDetailsModal
          flags={thinLineCheck.flags}
          initialUid={thinLineModalUid}
          onClose={() => setThinLineModalUid(null)}
        />
      )}
    </div>
  );
}
